/**
 * Подкасти засновника — слухаємо прямо в аппі (owner 2026-08-09).
 *
 * Три речі, на яких цей екран уже спіткнувся і які тут навмисно зроблені саме так:
 *
 * 1. Сторінка НЕ малює ні власний фон, ні власний горизонтальний padding:
 *    `/podcasts` не в edge-to-edge списку Layout, тому Layout сам дає `px-5`.
 *    Свій фон давав видимий шов, свій px-5 — ширину, що не збігалась з іншими
 *    розділами (див. WIDTH CONTRACT у components/Layout.tsx).
 * 2. Назва обрізається двома рядками. ⚠️ БЕЗ класу `block`: він перебиває
 *    `display:-webkit-box`, який ставить сам `line-clamp-2`, і обрізання
 *    мовчки не працює — картка розтягувалась на півекрана.
 * 3. Плеєр свій, а не нативний `controls`: системна сіра плашка не читається
 *    як частина продукту, а на iOS ще й показувала «Ошибка» на ogg.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BackNav } from "@/components/BackNav";
import { PageHeader } from "@/components/PageHeader";
import { Section } from "@/components/Section";
import { PageLoadError } from "@/components/LockedPage";
import { Spinner } from "@/components/Spinner";
import { usePodcasts } from "@/api/hooks";
import type { PodcastOut } from "@/api/hooks";
import { tg } from "@/lib/telegram";

function fmt(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

const SPEEDS = [1, 1.25, 1.5, 2] as const;

export function PodcastsPage() {
  const q = usePodcasts();
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<number | null>(null);

  if (q.isLoading) return <Spinner />;
  if (q.isError) return <PageLoadError error={q.error} section="knowledge_base" />;

  const items = q.data ?? [];

  return (
    <>
      {/* Вкладений розділ: «Назад» ліворуч, «Меню» праворуч — як в інших
          підрозділах. Раніше кнопки «Назад» тут не було взагалі. */}
      <BackNav onBack={() => navigate("/knowledge-base")} showMenu />

      <PageHeader
        eyebrow="база знань"
        title="Подкасти засновника"
        titleLead="Подкасти"
        titleAccent="засновника."
        scribbleWidth={230}
        subtitle="Досвід, продажі та клієнти — без води й теорії. Слухай прямо тут, у застосунку."
      />

      {items.length === 0 ? (
        <div className="pt-2 text-[0.8125rem] text-ink-dim [text-wrap:balance]">
          Подкасти зʼявляться тут найближчим часом.
        </div>
      ) : (
        <Section>
          {items.map((p, i) => (
            <PodcastCard
              key={p.id}
              podcast={p}
              isLast={i === items.length - 1}
              isActive={activeId === p.id}
              onActivate={() => setActiveId(p.id)}
            />
          ))}
        </Section>
      )}
    </>
  );
}

function PlayIcon({ playing }: { playing: boolean }) {
  // Дві причини, чому іконка «не по центру», і обидві вирішені тут:
  //  • svg за замовчуванням inline → додається місце під baseline, значок
  //    зʼїжджає вниз. Лікує `block`.
  //  • у трикутника центр обмежувального прямокутника правіше за оптичний
  //    центр, тому шлях додатково зсунуто вліво на 1 (owner 2026-08-09).
  return playing ? (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="block"
      aria-hidden
    >
      <rect x="7.2" y="5" width="3.6" height="14" rx="1.3" />
      <rect x="13.2" y="5" width="3.6" height="14" rx="1.3" />
    </svg>
  ) : (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="block"
      aria-hidden
    >
      <path
        transform="translate(-1.1 0)"
        d="M9.2 6.3 18 11.6a.5.5 0 0 1 0 .85L9.2 17.7A.5.5 0 0 1 8.45 17.3V6.7a.5.5 0 0 1 .75-.43Z"
      />
    </svg>
  );
}


function PodcastCard({
  podcast,
  isActive,
  isLast,
  onActivate,
}: {
  podcast: PodcastOut;
  isActive: boolean;
  isLast: boolean;
  onActivate: () => void;
}) {
  const ref = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [dur, setDur] = useState(podcast.duration_sec ?? 0);
  const [speed, setSpeed] = useState<number>(1);
  const [failed, setFailed] = useState(false);

  const posKey = `podcast_pos_${podcast.id}`;
  const started = playing || pos > 0;

  // Грає завжди один трек: запустив інший — цей став на паузу.
  useEffect(() => {
    if (!isActive && playing) {
      ref.current?.pause();
      setPlaying(false);
    }
  }, [isActive, playing]);

  const toggle = () => {
    tg()?.HapticFeedback?.selectionChanged?.();
    onActivate();
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      const saved = Number(localStorage.getItem(posKey) || 0);
      if (saved > 5 && el.currentTime < 1) el.currentTime = saved;
      el.playbackRate = speed;
      void el.play().catch(() => setFailed(true));
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const seekBy = (delta: number) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(el.duration || dur, el.currentTime + delta));
    tg()?.HapticFeedback?.selectionChanged?.();
  };

  const cycleSpeed = () => {
    const idx = SPEEDS.indexOf(speed as (typeof SPEEDS)[number]);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (ref.current) ref.current.playbackRate = next;
    tg()?.HapticFeedback?.selectionChanged?.();
  };

  const progress = dur > 0 ? Math.min(100, (pos / dur) * 100) : 0;

  return (
    <div className={isLast ? "" : "border-b border-line"}>
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center gap-3 px-3 py-3 text-left active:bg-surface-soft"
      >
        {/* Кругла помаранчева кнопка — власник схвалив саме такий вигляд. */}
        <span
          aria-label={playing ? "Пауза" : "Слухати"}
          className={`grid h-10 w-10 shrink-0 place-items-center rounded-full text-white transition ${
            playing
              ? "bg-primary shadow-[0_3px_10px_rgba(254,107,4,0.35)]"
              : "bg-primary/90"
          }`}
        >
          <PlayIcon playing={playing} />
        </span>

        {/* Три рядки: номер → назва → час. Саме цю структуру власник назвав
            вдалою (2026-08-09), тому не міняємо: номер тримає нумерацію
            замість колонки зліва, назва — звичайною вагою у два рядки. */}
        <div className="min-w-0 flex-1">
          <div className="font-display text-[0.65625rem] font-bold uppercase tracking-[0.14em] text-primary">
            Подкаст №{podcast.number ?? "—"}
          </div>
          <div className="mt-1 text-[0.84375rem] font-normal leading-[1.35] text-ink line-clamp-2">
            {podcast.title}
          </div>
          <div className="mt-1.5 font-mono text-[0.65625rem] tabular-nums tracking-[0.06em] text-ink-dim">
            {started ? `${fmt(pos)} / ${fmt(dur)}` : fmt(dur)}
          </div>
        </div>
      </button>

      {started && (
        <div className="px-3 pb-3">
          <input
            type="range"
            min={0}
            max={Math.max(1, dur)}
            value={pos}
            onChange={(e) => {
              const v = Number(e.target.value);
              setPos(v);
              if (ref.current) ref.current.currentTime = v;
            }}
            aria-label="Перемотка"
            className="podcast-range w-full"
            style={{
              background: `linear-gradient(90deg, rgb(254 107 4) ${progress}%, rgb(0 0 0 / 0.10) ${progress}%)`,
            }}
          />

          <div className="mt-2 flex items-center justify-center gap-1.5">
            <button
              type="button"
              onClick={() => seekBy(-15)}
              className="kx-press rounded-full bg-surface-soft px-2.5 py-1 font-mono text-[0.65625rem] tabular-nums text-ink-dim"
            >
              −15с
            </button>
            <button
              type="button"
              onClick={cycleSpeed}
              className="kx-press rounded-full bg-primary/10 px-2.5 py-1 font-display text-[0.65625rem] font-bold text-primary"
            >
              {speed}×
            </button>
            <button
              type="button"
              onClick={() => seekBy(15)}
              className="kx-press rounded-full bg-surface-soft px-2.5 py-1 font-mono text-[0.65625rem] tabular-nums text-ink-dim"
            >
              +15с
            </button>
          </div>

          {failed && (
            <div className="mt-2 text-center text-[0.71875rem] text-danger [text-wrap:balance]">
              Не вдалося відтворити. Онови застосунок і спробуй ще раз.
            </div>
          )}
        </div>
      )}

      <audio
        ref={ref}
        src={podcast.url}
        preload="none"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d) && d > 0) setDur(d);
        }}
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          setPos(t);
          localStorage.setItem(posKey, String(Math.floor(t)));
        }}
        onEnded={() => {
          setPlaying(false);
          setPos(0);
          localStorage.removeItem(posKey);
        }}
        onError={() => setFailed(true)}
      />
    </div>
  );
}
